import { FeatureGroup, useMap } from "react-leaflet";
import {
  leafletLayer,
  type PaintRule,
  PolygonSymbolizer,
} from "protomaps-leaflet";
import { useCallback, useEffect, useRef, useState } from "react";
import { type GridLayer, type LayersControlEvent } from "leaflet";
import { LAYER_Z_INDEX } from "@/features/map/constants/layerZIndex";
import { useProtomapsPrintAdapter } from "@/features/map/hooks/useProtomapsPrintAdapter";
import type { PrintRenderContext } from "@/features/map/utils/printRenderScale";

interface Props {
  defaultVisible: boolean;
  visible?: boolean;
}

// UIに表示するレイヤー名
export const DISASTER_RISK_AREA_LAYER_NAME = "災害危険区域";

// pmtilesが内部で保持しているレイヤー名
const DISASTER_RISK_AREA_DATA_LAYERS = [
  { name: "flood-river", fill: "rgb(107 161 213)" },
  { name: "flood-sea", fill: "rgb(20 128 254)" },
  { name: "flood-river-sea", fill: "rgb(13 75 254)" },
  { name: "steep-landslide", fill: "#FFED4C" },
  { name: "landslide", fill: "#FFB74C" },
  { name: "volcano", fill: "rgb(255 51 14)" },
  { name: "other", fill: "rgb(212 212 212)" },
];

const host = process.env.VITE_COMMON_TILE_HOST ?? "";
const url = `${host}/disaster-risk-area/a48_2021_z8_z18_layered_compliant.pmtiles`;

// ズームレベル
// pmtiles metadata: minzoom=8, maxzoom=18
export const DISASTER_RISK_AREA_MIN_ZOOM = 8;
export const DISASTER_RISK_AREA_MAX_ZOOM = 18;
const MIN_ZOOM = DISASTER_RISK_AREA_MIN_ZOOM;
const MAX_ZOOM = DISASTER_RISK_AREA_MAX_ZOOM;

// ポリゴンのスタイルを設定
const FILL_OPACITY = 0.7;
const STROKE_WIDTH = 4;

// 右下に表示する項目
const attribution =
  '<a href="https://nlftp.mlit.go.jp/ksj/other/agreement.html">国土数値情報</a>';

export const buildDisasterRiskAreaPaintRules = (
  renderScale: number,
  zoomOffset: number
): PaintRule[] =>
  DISASTER_RISK_AREA_DATA_LAYERS.map(({ name, fill }) => ({
    dataLayer: name,
    symbolizer: new PolygonSymbolizer({
      fill,
      opacity: FILL_OPACITY,
      stroke: fill,
      width: STROKE_WIDTH * renderScale,
    }),
    minzoom: MIN_ZOOM + zoomOffset,
    maxzoom: MAX_ZOOM + zoomOffset,
  }));

/**
 * 災害危険区域レイヤー
 */
export const DisasterRiskAreaLayer: React.FC<Props> = ({
  defaultVisible,
  visible: visibleProp,
}) => {
  const map = useMap();
  const [visible, setVisible] = useState(defaultVisible);
  const layerRef = useRef<GridLayer | null>(null);
  const createPrintLayer = useCallback(
    (context: PrintRenderContext): GridLayer =>
      leafletLayer({
        url,
        paintRules: buildDisasterRiskAreaPaintRules(
          context.printScale,
          context.zoomOffset
        ),
        attribution,
      }) as GridLayer,
    []
  );

  useProtomapsPrintAdapter({
    map,
    enabled: visible,
    normalLayerRef: layerRef,
    createPrintLayer,
  });

  useEffect(() => {
    if (!map) return;

    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
      return;
    }

    const handleOverlayAdd = (e: LayersControlEvent): void => {
      if (e.name === DISASTER_RISK_AREA_LAYER_NAME) {
        setVisible(true);
      }
    };

    const handleOverlayRemove = (e: LayersControlEvent): void => {
      if (e.name === DISASTER_RISK_AREA_LAYER_NAME) {
        setVisible(false);
      }
    };

    map.on("overlayadd", handleOverlayAdd);
    map.on("overlayremove", handleOverlayRemove);

    return () => {
      map.off("overlayadd", handleOverlayAdd);
      map.off("overlayremove", handleOverlayRemove);
    };
  }, [map, visibleProp]);

  useEffect(() => {
    if (!map) return;

    const layer = leafletLayer({
      url,
      paintRules: buildDisasterRiskAreaPaintRules(1, 0),
      attribution,
    }) as GridLayer;

    layer.setZIndex(LAYER_Z_INDEX.DISASTER_RISK_AREA);
    layerRef.current = layer;

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
    }
  }, [visibleProp]);

  useEffect(() => {
    if (!map || !layerRef.current) return;

    if (visible) {
      layerRef.current.addTo(map);
    } else {
      map.removeLayer(layerRef.current);
    }
  }, [map, visible]);

  return <FeatureGroup />;
};
